import {getTurns, type TimelineTurn} from '@github-ui/ahp-relay/ahp-session-selectors'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import type {ChatState, SessionState as AhpSessionState} from '@microsoft/agent-host-protocol'
import {useTaskAhpHistory} from '../../hooks/use-task-ahp-history'
import type {Session} from '../../types/session'

type ResolvedAhpAgent = {kind: 'default'} | {kind: 'selected'; displayName: string} | {kind: 'unavailable'}

export type SessionAgentResolution = ResolvedAhpAgent | {kind: 'loading'}

function latestUserTurn(
  chatState: ChatState | undefined,
  savedTurns?: readonly TimelineTurn[],
): TimelineTurn | undefined {
  return (savedTurns ?? (chatState ? getTurns(chatState) : [])).findLast(turn => turn.message.origin?.kind === 'user')
}

export function resolveSelectedAhpAgent(
  chatState: ChatState | undefined,
  sessionState: AhpSessionState | undefined,
  savedTurns?: readonly TimelineTurn[],
  fallbackSessionState?: AhpSessionState,
): ResolvedAhpAgent {
  const userTurn = latestUserTurn(chatState, savedTurns)
  if (!userTurn) return {kind: 'unavailable'}
  const selectedUri = userTurn.message.agent?.uri
  if (!selectedUri) return {kind: 'default'}

  for (const state of [sessionState, fallbackSessionState]) {
    for (const customization of state?.customizations ?? []) {
      if (!('children' in customization)) continue
      const agent = customization.children?.find(child => child.type === 'agent' && child.uri === selectedUri)
      if (agent?.name?.trim()) return {kind: 'selected', displayName: agent.name}
    }
  }

  return {kind: 'unavailable'}
}

export function useSessionAgentResolution({
  session,
  isAhpSession,
  detailsVisible,
  liveChatState,
  liveSessionState,
  apiURL,
}: {
  session?: Session
  isAhpSession: boolean
  detailsVisible: boolean
  liveChatState?: ChatState
  liveSessionState?: AhpSessionState
  apiURL?: string
}): SessionAgentResolution | undefined {
  const establishedSession = useOptionalEstablishedAhpSession()
  const matchingLiveSession =
    session &&
    establishedSession?.identity.sessionId === session.id &&
    establishedSession.identity.taskId === session.task_id
  const matchingLiveChatState = matchingLiveSession ? liveChatState : undefined
  const matchingLiveSessionState = matchingLiveSession ? liveSessionState : undefined
  const liveTurns = matchingLiveChatState ? getTurns(matchingLiveChatState) : undefined
  const liveUserTurn = latestUserTurn(undefined, liveTurns)
  const liveAgent = matchingLiveChatState
    ? resolveSelectedAhpAgent(matchingLiveChatState, matchingLiveSessionState, liveTurns)
    : undefined
  const history = useTaskAhpHistory(
    session?.task_id,
    apiURL,
    detailsVisible && isAhpSession && liveAgent?.kind !== 'selected' && liveAgent?.kind !== 'default',
  )

  if (!isAhpSession) return undefined
  if (liveAgent?.kind === 'selected' || liveAgent?.kind === 'default') return liveAgent
  if (!session?.task_id) return {kind: 'unavailable'}

  const bundle = history.data?.find(saved => saved.sessionId === session.id)?.bundle
  const savedChatState = bundle?.defaultChatState
  const savedTurns = savedChatState ? bundle?.timelineTurns?.[savedChatState.resource] : undefined
  const savedAgent = bundle
    ? resolveSelectedAhpAgent(savedChatState, matchingLiveSessionState, savedTurns, bundle.sessionState)
    : undefined
  const liveAgentWithSavedCatalog =
    matchingLiveChatState && bundle
      ? resolveSelectedAhpAgent(matchingLiveChatState, matchingLiveSessionState, liveTurns, bundle.sessionState)
      : undefined

  if (liveAgentWithSavedCatalog?.kind === 'selected') return liveAgentWithSavedCatalog
  // A saved selection must not replace a newer agent (or provider default) selected in the live chat.
  if (!liveUserTurn && savedAgent?.kind === 'selected') return savedAgent
  if (history.isPending && history.fetchStatus === 'idle') return liveAgent ?? {kind: 'unavailable'}
  if (history.isPending || history.isFetching) return {kind: 'loading'}
  if (liveAgent?.kind === 'unavailable' || savedAgent?.kind === 'unavailable' || history.isError || !bundle) {
    return {kind: 'unavailable'}
  }
  return {kind: 'default'}
}
